import { useEffect, useRef, useState } from 'react';
import { CITIES, countryFlag, formatMoney, type CargoMapPoint } from 'shared';
import { api } from './api';
import { cellSizeForZoom, clusterByGrid } from './cluster';
import { addThemedTileLayer, loadLeaflet, locateUser } from './leaflet';
import { haptic } from './telegram';

interface Props {
  /** Те же фильтры, что стоят в ленте: карта показывает тот же отбор. */
  query: Record<string, string | number | boolean | undefined>;
  onOpen: (id: string) => void;
  height?: number;
}

/** Куда смотреть, когда заявок нет вовсе: середина европейской части. */
const DEFAULT_CENTER: [number, number] = [55.75, 44.0];
const DEFAULT_ZOOM = 4;

/**
 * Карта заявок.
 *
 * Метка стоит в точке погрузки — искать работу начинают с того, откуда
 * ехать. Направление показывается по нажатию: линия до выгрузки рисуется
 * только для выбранной заявки, иначе на карте страны получается моток
 * ниток, из которого ничего не выбрать.
 *
 * Близкие метки при отдалении собираются в кружок с числом (`cluster.ts`):
 * на масштабе страны Москва с Химками — одна точка шириной в пиксель.
 */
export default function CargoMap({ query, onOpen, height = 420 }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const layer = useRef<any>(null);
  /** Линия выбранного направления. Живёт отдельно: её стирают при новом выборе. */
  const route = useRef<any>(null);
  /** Счётчик для отмены устаревшего ответа маршрутизации при быстром переклике. */
  const routeRequest = useRef(0);

  const [points, setPoints] = useState<CargoMapPoint[]>([]);
  const [capped, setCapped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [locating, setLocating] = useState(false);
  /**
   * Заявки в одной точке карты.
   *
   * Метка стоит в центре города, поэтому у пяти московских заявок
   * координаты буквально совпадают: приближение их не разводит, а зум
   * упирается в потолок. Раньше нажатие на кружок с числом только
   * приближало — число было видно, а добраться до заявок нельзя.
   */
  const [stack, setStack] = useState<CargoMapPoint[] | null>(null);
  const me = useRef<any>(null);

  // Заявки и библиотека грузятся независимо: карта пустая до тех пор,
  // пока не готово и то и другое, но ждать их по очереди незачем.
  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    api
      .cargoMap(query)
      .then((r) => {
        if (cancelled) return;
        setPoints(r.items);
        setCapped(r.capped);
      })
      .catch((e: Error) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(query)]);

  useEffect(() => {
    let cancelled = false;

    loadLeaflet()
      .then(() => !cancelled && setReady(true))
      .catch((e: Error) => !cancelled && setError(e.message));

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || !box.current) return;
    const L = window.L;

    if (!map.current) {
      map.current = L.map(box.current, { zoomControl: true });
      addThemedTileLayer(map.current);
      map.current.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    }

    /*
     * Кружки пересобираются на каждом изменении масштаба: их состав
     * зависит только от него, хранить между кадрами нечего.
     *
     * Подписка переставляется на каждый заход, а не один раз при создании
     * карты. Раньше она стояла внутри `if (!map.current)` и навсегда
     * запоминала `draw` из первого прохода — а тогда заявок ещё нет, они
     * приезжают запросом позже. Метки появлялись (следующий заход звал
     * `draw` напрямую), но первый же зум вызывал старый обработчик с
     * пустым списком и стирал их до следующей перерисовки.
     */
    map.current.on('zoomend', draw);

    draw();

    // Карта строится внутри модального окна и не знает своих размеров,
    // пока его не показали. Без этого видно один серый квадрат.
    const t = setTimeout(() => map.current?.invalidateSize(), 80);
    return () => {
      clearTimeout(t);
      map.current?.off('zoomend', draw);
    };

    function draw() {
      if (!map.current) return;

      layer.current?.remove();
      layer.current = L.layerGroup().addTo(map.current);

      const zoom = map.current.getZoom();
      const clusters = clusterByGrid(
        points.map((p) => ({ ...p, lat: p.fromLat, lng: p.fromLng })),
        cellSizeForZoom(zoom),
      );

      for (const c of clusters) {
        if (c.items.length === 1) {
          addPin(c.items[0]!);
          continue;
        }

        // Число вместо стопки меток. При наведении — что там лежит,
        // при нажатии — список, из которого заявку можно открыть.
        // Наведения на телефоне нет, поэтому нажатие обязано давать всё
        // то же самое и сверх того.
        const preview = c.items
          .slice(0, 6)
          .map(
            (p) =>
              `${escapeHtml(p.fromAddress)} → ${escapeHtml(p.toAddress)} · ${formatMoney(p.price, p.currency)}`,
          )
          .join('<br>');

        L.marker([c.lat, c.lng], {
          icon: L.divIcon({
            className: 'map-cluster',
            html: `<span>${c.items.length}</span>`,
            iconSize: [36, 36],
          }),
        })
          .bindTooltip(
            preview + (c.items.length > 6 ? `<br>…и ещё ${c.items.length - 6}` : ''),
            { direction: 'top', offset: [0, -18] },
          )
          .on('click', () => {
            haptic('light');
            setStack(c.items);
          })
          .addTo(layer.current);
      }

      function addPin(p: CargoMapPoint & { lat: number; lng: number }) {
        const marker = L.circleMarker([p.lat, p.lng], {
          radius: 8,
          weight: 2,
          fillOpacity: 0.9,
          className: p.partial ? 'map-pin map-pin--partial' : 'map-pin',
        }).addTo(layer.current);

        marker.bindPopup(
          `<strong>${cityFlag(p.fromAddress)}${escapeHtml(p.fromAddress)} → ` +
            `${cityFlag(p.toAddress)}${escapeHtml(p.toAddress)}</strong>` +
            `<br>${escapeHtml(p.title)} · ${(p.weightKg / 1000).toFixed(1)} т` +
            `${p.distanceKm ? ` · ${p.distanceKm} км` : ''}` +
            `<br><b>${formatMoney(p.price, p.currency)}</b>` +
            `<br><a href="#" data-cargo="${p.id}">Открыть заявку №&nbsp;${escapeHtml(p.number)}</a>`,
        );

        // Линия до выгрузки — только у выбранной заявки. Иначе карта
        // страны превращается в моток ниток.
        //
        // Сначала прямая (мгновенно, без сети) — затем, если сервис
        // маршрутизации настроен и ответил, заменяем её на маршрут по
        // дорогам с учётом грузового профиля. Запрос отменяется отбором
        // по актуальности: быстрый повторный клик не даст старому ответу
        // перерисовать уже выбранную заявку.
        marker.on('click', () => {
          route.current?.remove();
          route.current = L.polyline(
            [
              [p.fromLat, p.fromLng],
              [p.toLat, p.toLng],
            ],
            { weight: 3, opacity: 0.6, dashArray: '6 6' },
          ).addTo(layer.current);

          const requestId = ++routeRequest.current;
          api
            .cargoRoute(p.id)
            .then((r) => {
              if (requestId !== routeRequest.current || !r.points) return;
              route.current?.remove();
              route.current = L.polyline(
                r.points.map((pt) => [pt.lat, pt.lng]),
                { weight: 4, opacity: 0.8 },
              ).addTo(layer.current);
            })
            .catch(() => {
              // Маршрут необязателен — прямая линия уже показана, тихо оставляем её.
            });
        });

        marker.on('popupopen', (e: any) => {
          const link = e.popup.getElement()?.querySelector('a[data-cargo]');
          link?.addEventListener('click', (ev: Event) => {
            ev.preventDefault();
            haptic();
            onOpen(p.id);
          });
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, points]);

  useEffect(
    () => () => {
      map.current?.remove();
      map.current = null;
    },
    [],
  );

  function handleLocate() {
    if (!map.current || !window.L) return;
    setLocating(true);
    haptic('light');
    locateUser()
      .then(({ lat, lng }) => {
        const L = window.L;
        me.current?.remove();
        me.current = L.circleMarker([lat, lng], {
          radius: 7,
          weight: 3,
          className: 'map-pin map-pin--me',
        }).addTo(map.current);
        map.current.setView([lat, lng], 11);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLocating(false));
  }

  if (error) return <div className="empty">{error}</div>;

  return (
    <>
      <div className="meta" style={{ marginBottom: 8 }}>
        {loading
          ? 'Собираю заявки…'
          : points.length === 0
            ? 'По текущим фильтрам заявок нет.'
            : `На карте ${points.length} заявок${capped ? ' — показаны не все, сузьте фильтры' : ''}`}
      </div>
      <div className="map-wrap">
        <div ref={box} className="trackmap" style={{ height }} />

        {stack && (
          <div className="map-stack">
            <div className="map-stack__head">
              <strong>{stack.length} заявки в этой точке</strong>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setStack(null)}
                aria-label="Закрыть список"
              >
                ×
              </button>
            </div>
            <div className="map-stack__list">
              {stack.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="map-stack__item"
                  onClick={() => {
                    haptic();
                    setStack(null);
                    onOpen(p.id);
                  }}
                >
                  <span className="map-stack__route">
                    {p.fromAddress} → {p.toAddress}
                  </span>
                  <span className="meta">
                    {(p.weightKg / 1000).toFixed(1)} т
                    {p.distanceKm ? ` · ${p.distanceKm} км` : ''}
                    {p.partial ? ' · догруз' : ''}
                  </span>
                  <strong>{formatMoney(p.price, p.currency)}</strong>
                </button>
              ))}
            </div>
          </div>
        )}
        <button
          type="button"
          className="icon-btn map-locate-btn"
          onClick={handleLocate}
          disabled={locating}
          title="Моё местоположение"
          aria-label="Моё местоположение"
        >
          {locating ? '…' : '◎'}
        </button>
      </div>
      <div className="meta" style={{ marginTop: 8 }}>
        Метка стоит в центре города: точный адрес виден в карточке заявки.
      </div>
    </>
  );
}

/**
 * Флаг страны города для всплывающей плашки.
 *
 * Считается из кода арифметикой по Unicode (`countryFlag`), а не берётся
 * из `COUNTRIES`: тот знает десять стран СНГ, и груз из Техаса оставался
 * без флага. Пусто, если города нет в справочнике, — тогда кода взять
 * неоткуда.
 */
function cityFlag(address: string): string {
  const flag = countryFlag(CITIES[address]?.country);
  return flag ? `${flag} ` : '';
}

/**
 * Подстановка в HTML всплывающей плашки. Leaflet принимает строку, а не
 * элементы, поэтому чужой текст — название груза, адрес — экранируется
 * руками: иначе кавычка в адресе ломает вёрстку, а тег — и всё остальное.
 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
